import * as Sentry from '@sentry/angular';
import type * as fromDesignSystem from '@serious-system';
import type { Environment } from '../../environments/environment.type';

const idpIconMap: Record<string, fromDesignSystem.SocialIconsType> = {
  'entra-id': 'microsoft-no-bg',
  'google-workspace': 'google-no-bg',
};
const idpProviderNameMap: Record<string, string> = {
  'entra-id': 'Microsoft',
  'google-workspace': 'Google',
};

class ParseCognitoIdpConfigError extends Error {
  constructor(message: string) {
    super(`Error parsing cognito idp config: ${message}`);
    this.name = 'ParseCognitoIdpConfigError';
  }
}

class UnsupportedIdentityProviderError extends Error {
  constructor(idp: string) {
    super(`Unsupported identity provider configured: ${idp}`);
    this.name = 'UnsupportedIdentityProviderError';
  }
}

export interface LoginButtonConfig {
  idp: string;
  idpCustomId: string;
  idpLogo: fromDesignSystem.SocialIconsType;
  idpLabel: string;
  idpDomain: null | string;
}

const isLoginButtonConfig = (
  config: LoginButtonConfig | null,
): config is LoginButtonConfig => {
  return config !== null;
};

export const cognitoIdpConfigToLoginButtonConfigs = (
  cognitoIdpConfig: Environment['cognitoIdpConfig'],
) => {
  try {
    return cognitoIdpConfig()
      .map((config) => {
        const idpLogo = idpIconMap[config.idp];
        const idpLabel = idpProviderNameMap[config.idp];

        if (!idpLogo || !idpLabel) {
          const error = new UnsupportedIdentityProviderError(config.idp);
          Sentry.captureException(error);
          return null;
        }

        return {
          idp: config.idp,
          idpCustomId: config.idpCustomId,
          idpLogo,
          idpLabel,
          idpDomain: config.domain,
        };
      })
      .filter(isLoginButtonConfig);
  } catch (error) {
    if (error instanceof Error) {
      const sentryError = new ParseCognitoIdpConfigError(error.message);
      Sentry.captureException(sentryError);
      throw sentryError;
    }
    throw error;
  }
};

/**
 * SB-3337 T4 — the bespoke "Continue with HS Google" login button.
 *
 * Returns a single {@link LoginButtonConfig} when the `?identityProvider` query
 * param matches the HalfSerious-support IdP name (`environment.hsIdpCustomId`),
 * otherwise an empty array — so the button is appended to the client's normal
 * SSO buttons only on that match (KD-6), and never rendered without it (KD-4).
 *
 * The label reuses the existing `LOGIN.CTA_CONTINUE_WITH` key with the brand
 * token "HS Google" (like the Microsoft/Google buttons), not a bespoke key —
 * see the plan's KD-7 note.
 */
export const hsLoginButtonConfigIfRequested = (
  identityProviderParam: string | null,
  hsIdpCustomId: string,
): LoginButtonConfig[] =>
  identityProviderParam !== hsIdpCustomId
    ? []
    : [
        {
          idp: 'google-workspace',
          idpCustomId: hsIdpCustomId,
          idpLogo: 'google-no-bg',
          idpLabel: 'HS Google',
          idpDomain: null,
        },
      ];
